import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { connect } from "react-redux";
import { addItemSolicitud, deleteItemSolicitud } from "../../../redux/actions/solicitud";
import ProductDetailModal from '../ProductDetailModal';
import ImageCarousel from '../../ui/ImageCarousel';
import agregarSvg from '../../../images/agregar.svg';
import noImageIcon from '../../../images/no-image-icon.svg';
import { mediaUrl } from '../../../api/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import styles from './ProductCard.module.css';
import cards from '../../../styles/shared/ProductCards.module.css';
const ProductCard = React.memo(({ carrito, producto, itemsCarrito, addItemSolicitud, deleteItemSolicitud }) => {
  const [cantidad, setCantidad] = useState(0);
  const [showAddButton, setShowAddButton] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const cardRef = useRef(null);
  const imageRef = useRef(null);

  const { imageUrl, thumbnailUrl, imagesUrl } = useMemo(() => {
    if (producto.images && producto.images.length > 0) {
      return {
        thumbnailUrl: mediaUrl(producto.images[0].thumbnail),
        imageUrl: mediaUrl(producto.images[0].image),
        imagesUrl: producto.images.map(img => mediaUrl(img.image))
      };
    } else {
      return {
        thumbnailUrl: null,
        imageUrl: null,
        imagesUrl: [noImageIcon]
      };
    }
  }, [producto.images]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1
      }
    );

    const node = cardRef.current;
    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, []);

  const handleImageLoad = useCallback(() => {
    setIsImageLoaded(true);
  }, []);

  useEffect(() => {
    const img = imageRef.current;

    if (isVisible && imageUrl && img) {
      if (img.complete) {
        handleImageLoad();
      } else {
        img.addEventListener('load', handleImageLoad);
      }

      return () => {
        if (img) {
          img.removeEventListener('load', handleImageLoad);
        }
      };
    }
  }, [isVisible, imageUrl, handleImageLoad]);
  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);

  useEffect(() => {
    if (itemsCarrito) {
      const itemInCarrito = itemsCarrito.find(item => item.producto.id === producto.id);
      if (itemInCarrito) {
        setCantidad(itemInCarrito.cantidad);
        setShowAddButton(true);
      } else {
        setCantidad(0);
        setShowAddButton(false);
      }
    } else {
      setCantidad(0);
      setShowAddButton(false);
    }
  }, [itemsCarrito, producto.id, carrito]);

  const incrementarCantidad = useCallback(() => {
    if (cantidad < producto.cantidad) {
      addItemSolicitud(producto);
    } else {
      console.error(`No se puede agregar más del producto ${producto.id}. Cantidad máxima alcanzada.`);
    }
  }, [cantidad, producto, addItemSolicitud]);

  const decrementarCantidad = useCallback(() => {
    if (cantidad > 0) {
      const nuevaCantidad = cantidad - 1;
      setCantidad(nuevaCantidad);
      deleteItemSolicitud(producto.id);
      if (nuevaCantidad === 0) {
        setShowAddButton(false);
      }
    }
  }, [cantidad, producto.id, deleteItemSolicitud]);

  const isOutOfStock = producto.cantidad === 0 || producto.cantidad === null;

  const addButtonClick = useCallback(() => {
    setShowAddButton(true);
    incrementarCantidad();
  }, [setShowAddButton, incrementarCantidad]);

  const renderAddButton = useMemo(() => {
    if (showAddButton) {
      return (
        <div className={styles.addButtonProducto}>
          <button onClick={decrementarCantidad}>
            <FontAwesomeIcon icon={faTrash} className={styles.quitarIcon} title="Quitar producto" />
          </button>
          <span>{cantidad}</span>
          <button onClick={incrementarCantidad}>
            <img src={agregarSvg} alt='Agregar producto' />
          </button>
        </div>
      );
    } else if (!isOutOfStock) {
      return (
        <div className={cards.addButton} onClick={addButtonClick}>Agregar</div>
      );
    }
    return null;
  }, [showAddButton, isOutOfStock, cantidad, decrementarCantidad, incrementarCantidad, addButtonClick]);

  return (
    <div className={`${styles.wrapper} ${isOutOfStock ? styles.outOfStock : ''}`} ref={cardRef}>
      <div className={styles.productCard} onClick={openModal}>
        <div className={styles.imageContainer}>
          {thumbnailUrl && (
            <img 
              src={thumbnailUrl} 
              alt={producto.equipo || 'Producto'} 
              className={`${styles.image} ${styles.thumbnail}`}
            />
          )}
          {imageUrl && (
            <img 
              ref={imageRef}
              src={imageUrl} 
              alt={producto.equipo || 'Producto'} 
              className={`${styles.image} ${styles.fullImage} ${isImageLoaded ? styles.loaded : ''}`}
            />
          )}
          {!thumbnailUrl && !imageUrl && (
            <img src={noImageIcon} alt="No image available" className={`${styles.noImage} ${styles.image}`} />
          )}
        </div>
        {isOutOfStock && <div className={styles.productOverlay}><p>No disponible</p></div>}
        
        <h6 className={cards.cardTitle2}>{producto.marca ? producto.marca : 'Marca no disponible'}</h6>
                <div className={`card-body ${cards.cardBody}`}>
                    <h5 className={`card-title ${cards.cardTitle}`}>{producto.equipo ? producto.equipo : 'Nombre no disponible'}</h5>
                        <div className={cards.iconButtons}>
                             <p className={`card-text ${cards.cardText}`}><b>{producto.cantidad}</b></p>
                    </div>
                </div>
            </div>
      {renderAddButton}
     <ProductDetailModal isOpen={isModalOpen} onClose={closeModal}>
     {imagesUrl.length > 1 ? (
          <ImageCarousel images={imagesUrl} />
        ) : (
          <img src={imagesUrl[0]} alt={producto.equipo || 'Producto'} className={cards.modalImage} />
        )}
        {isOutOfStock && <div className={`${cards.overlay} text-center`}><p>No disponible</p></div>}
        <h2>{producto.equipo}</h2>
        <p><b>Marca:</b> {producto.marca || "No disponible"}</p>
        <p><b>Descripción:</b> {producto.descripcion || "No disponible"}</p>
        <p><b>Estado:</b> {producto.estado || "No disponible"}</p>
        <p><b>Sección:</b> {producto.seccion || "No disponible"}</p>
        <p><b>Fecha de Vencimiento:</b> {producto.fecha_vencimiento || "No disponible"}</p>
        <p><b>Proveedor:</b> {producto.proveedor || "No disponible"}</p>
        <p><b>Observación:</b> {producto.observacion || "No disponible"}</p>
        <p className={`card-text ${cards.cardText}`}><b>Cantidad: </b> {producto.cantidad} {producto.cantidad ? producto.cantidad >1 ? 'Unidades' : 'Unidad': 'No disponible'}</p>
        {showAddButton ? (
          <div className={styles.addButtonProducto}>
            <button onClick={decrementarCantidad}>
              <FontAwesomeIcon icon={faTrash} className={styles.quitarIcon} title="Quitar producto" />
            </button>
            <span>{cantidad}</span>
            <button onClick={incrementarCantidad}>
              <img src={agregarSvg} alt='Agregar producto' />
            </button>
          </div>
        ) : (
          isOutOfStock ? null : (
            <div className={cards.addButton} onClick={() => { setShowAddButton(true); incrementarCantidad(); }}>Agregar</div>
          )
        )}
      </ProductDetailModal>
    </div>
  );
});

const mapStateToProps = (state) => ({
  carrito: state.Solicitud.carrito,
});

export default connect(mapStateToProps, {
  addItemSolicitud,
  deleteItemSolicitud,
})(ProductCard);